/** Small presentational helpers. */
export function formatEuro(n) {
  if (n === null || n === undefined) return "—";
  return new Intl.NumberFormat("nl-BE", {
    style: "currency", currency: "EUR", maximumFractionDigits: 0,
  }).format(n);
}

export function formatDateShort(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return new Intl.DateTimeFormat("nl-BE", { day: "2-digit", month: "short" }).format(d);
}

/** Parse our DB date format (DD/MM/YYYY) — also tolerates ISO and DD-MM-YYYY. */
export function parseDate(s) {
  if (!s) return null;
  const str = String(s).trim();
  // DD/MM/YYYY or DD-MM-YYYY
  let m = str.match(/^(\d{1,2})[/\-](\d{1,2})[/\-](\d{4})$/);
  if (m) return new Date(`${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`);
  // ISO YYYY-MM-DD
  m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(m[0]);
  // last resort
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
}

/** Format a stored date for display in the Belgian locale (DD/MM/YYYY). */
export function formatDateBE(s) {
  const d = parseDate(s);
  if (!d) return "—";
  return new Intl.DateTimeFormat("nl-BE", {
    day: "2-digit", month: "2-digit", year: "numeric",
  }).format(d);
}

export function daysSince(dateStr) {
  const d = parseDate(dateStr);
  if (!d || isNaN(d.getTime())) return null;
  const ms = Date.now() - d.getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

/**
 * Friendly "online sinds" label used on the overview rows.
 *
 *  < 7 days   → "X dagen"
 *  < 5 weeks  → "X weken" (or "1 week")
 *  ≥ 5 weeks  → "X mnd" with .5 precision when below 12 months, integer above
 */
export function formatTimeSince(dateStr) {
  const d = daysSince(dateStr);
  if (d === null) return "—";
  if (d < 1) return "vandaag";
  if (d < 7) return `${d} ${d === 1 ? "dag" : "dagen"}`;
  const weeks = Math.floor(d / 7);
  if (weeks < 5) return `${weeks} ${weeks === 1 ? "week" : "weken"}`;
  const months = d / 30;
  if (months < 12) {
    const rounded = Math.round(months * 2) / 2; // 0.5-step
    return `${rounded.toString().replace(".", ",")} mnd`;
  }
  return `${Math.round(months)} mnd`;
}

/** Progress out of the typical mandate period.
 * Default = 6 months (makelaar exclusieve mandate).
 * For particulieren: 2 months (most owners switch to a makelaar after that).
 */
export function contractProgress(datePosted, sourceType = "makelaar") {
  const days = daysSince(datePosted);
  const totalMonths = sourceType === "particulier" ? 2 : 6;
  if (days === null) return { pct: 0, tone: "ok", monthsOut: 0, totalMonths };
  const months = days / 30;
  const pct = Math.min(100, (months / totalMonths) * 100);
  const dangerAt = totalMonths * (5 / 6); // ~83%
  const warnAt = totalMonths * (3.5 / 6); // ~58%
  const tone = months >= dangerAt ? "danger" : months >= warnAt ? "warn" : "ok";
  return {
    pct, tone, totalMonths,
    monthsOut: Math.min(totalMonths, Math.round(months * 10) / 10),
  };
}

export function greetingKey() {
  const h = new Date().getHours();
  if (h < 12) return "dashboard.greeting_morning";
  if (h < 18) return "dashboard.greeting_afternoon";
  return "dashboard.greeting_evening";
}
